import { api, unwrap } from '@/api'
import type {
  ApprovalRequest,
  PlatformStats,
  ReviewApprovalPayload,
  SubscriptionsOverview,
  UsersOverview,
} from '../types/admin.types'

export interface AdminPlanPayload {
  id?: number
  name: string
  slug: string
  description?: string
  planType: 'SELLER' | 'DEALER' | 'CUSTOMER'
  price: number
  monthlyPrice?: number
  quarterlyPrice?: number
  yearlyPrice?: number
  currency?: string
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY'
  trialDays?: number
  isPopular?: boolean
  isActive?: boolean
  color?: string
  icon?: string
  razorpayProductId?: string
  razorpayPlanId?: string
  paymentFrequency?: string
  trialSupported?: boolean
  renewalEnabled?: boolean
  cancellationPolicy?: string
  gracePeriodDays?: number
  features?: { featureKey: string; featureValue: string }[]
}

export interface AdminApprovalPayload extends ReviewApprovalPayload {
  assignmentType?: 'DEFAULT' | 'SPECIFIC' | 'NONE' | 'REQUIRE_PAYMENT' | 'TRIAL'
  planId?: number
  customTrialDays?: number
}

export interface FormConfigPayload {
  fields: any[]
  documents: any[]
  settings: any
}

export const adminService = {
  async getStats(): Promise<PlatformStats> {
    const res = await api.get<PlatformStats>('/platform/admin/stats')
    return unwrap(res)
  },

  async getPendingRequestsCount(): Promise<{ pendingSeller: number; pendingDealer: number }> {
    const res = await api.get<{ pendingSeller: number; pendingDealer: number }>('/platform/admin/requests')
    return unwrap(res)
  },

  async getPendingApprovals(): Promise<ApprovalRequest[]> {
    const res = await api.get<ApprovalRequest[]>('/platform/admin/approvals/pending')
    return unwrap(res)
  },

  async approveRequest(id: number, payload?: AdminApprovalPayload): Promise<ApprovalRequest> {
    const res = await api.post<ApprovalRequest>(`/platform/admin/approvals/${id}/approve`, payload ?? {})
    return unwrap(res)
  },

  async rejectRequest(id: number, payload?: ReviewApprovalPayload): Promise<ApprovalRequest> {
    const res = await api.post<ApprovalRequest>(`/platform/admin/approvals/${id}/reject`, payload ?? {})
    return unwrap(res)
  },

  async getUsersOverview(): Promise<UsersOverview> {
    const res = await api.get<UsersOverview>('/platform/admin/users')
    return unwrap(res)
  },

  async getUserActivity(userId: number): Promise<any> {
    const res = await api.get<any>(`/platform/admin/users/${userId}/activity`)
    return unwrap(res)
  },

  async getSubscriptionsOverview(): Promise<SubscriptionsOverview> {
    const res = await api.get<SubscriptionsOverview>('/platform/admin/subscriptions')
    return unwrap(res)
  },

  // Plan Management
  async createPlan(payload: AdminPlanPayload) {
    const res = await api.post('/platform/admin/plans', payload)
    return unwrap(res)
  },

  async updatePlan(id: number, payload: AdminPlanPayload) {
    const res = await api.put(`/platform/admin/plans/${id}`, payload)
    return unwrap(res)
  },

  async duplicatePlan(id: number) {
    const res = await api.post(`/platform/admin/plans/${id}/duplicate`)
    return unwrap(res)
  },

  async archivePlan(id: number) {
    const res = await api.patch(`/platform/admin/plans/${id}/archive`)
    return unwrap(res)
  },

  async deletePlan(id: number) {
    const res = await api.delete(`/platform/admin/plans/${id}`)
    return unwrap(res)
  },

  // Dynamic Form Builder
  async getFormConfig(formType: 'SELLER' | 'DEALER') {
    const res = await api.get(`/platform/admin/form-config/${formType}`)
    return unwrap(res)
  },

  async updateFormConfig(formType: 'SELLER' | 'DEALER', payload: FormConfigPayload) {
    const res = await api.put(`/platform/admin/form-config/${formType}`, payload)
    return unwrap(res)
  },

  // Settings & Audit Logs
  async getSettings() {
    const res = await api.get('/platform/admin/settings')
    return unwrap(res)
  },

  async updateSettings(settings: { key: string; value: any }[]) {
    const res = await api.put('/platform/admin/settings', { settings })
    return unwrap(res)
  },

  async getAuditLogs() {
    const res = await api.get('/platform/admin/audit-logs')
    return unwrap(res)
  },
}
